import moment from 'moment';
import type { ExportColumnHeader, ExportOptions, ICase, IForm } from '@/models';

export const PERIOD_TYPE = {
  ALL: 'All',
  SELECT: 'Select'
} as const;

export type PeriodType = typeof PERIOD_TYPE[keyof typeof PERIOD_TYPE];

export const FORMAT_TYPE = {
  CSV: 'csv',
  XLSX: 'xlsx'
} as const;

export type FormatType = typeof FORMAT_TYPE[keyof typeof FORMAT_TYPE];

export const CHOICE_TYPE = {
  CODED: 'coded',
  LABELS: 'labels'
} as const;

export type ChoiceType = typeof CHOICE_TYPE[keyof typeof CHOICE_TYPE];

export const CHECKBOX_VALUE = {
  ARCHIVE: 'archive',
  FILES: 'files',
  METADATA: 'metadata'
} as const;

export const MATRIX_HEADER = {
  COLUMNS: 'columns',
  ROWS: 'rows'
} as const;

export type MatrixHeader = typeof MATRIX_HEADER[keyof typeof MATRIX_HEADER];

export const COLUMN_HEADERS: ExportColumnHeader[] = [
  { label: 'Field Label', value: 'label' },
  { label: 'Field Variable Name', value: 'variableName' },
  { label: 'Field Identifier', value: 'identifier' }
];

export interface ExportFormState {
  selectedForm: IForm[];
  selectedCases: ICase[] | null;
  periodType: PeriodType | undefined;
  periodDates: [Date, Date] | null | undefined;
  formatType: FormatType | undefined;
  choiceType: ChoiceType | undefined;
  includeArchive: string[] | undefined;
  includeFiles: string[] | undefined;
  includeMetadata: string[] | undefined;
  separateMatrixSheets: boolean;
  matrixHeaderColumns: MatrixHeader;
  selectedTimezone: string | undefined;
  localTimezoneForSystemDates: boolean | undefined;
  selectedColumnHeader: ExportColumnHeader;
}

export const buildFormStateFromOptions = (
  opts: ExportOptions,
  availableForms: IForm[],
  availableCases: ICase[],
  columnHeaders: ExportColumnHeader[]
): Partial<ExportFormState> => {
  const state: Partial<ExportFormState> = {};

  if (opts.formIdentifiers?.length) {
    state.selectedForm = availableForms.filter(
      f => opts.formIdentifiers!.includes(f.identifier)
    ) as IForm[];
  }

  if (opts.caseIdentifiers?.length) {
    state.selectedCases = availableCases.filter(
      c => opts.caseIdentifiers!.includes(c.identifier)
    ) as unknown as ICase[];
  }

  if (opts.period) {
    state.periodType = PERIOD_TYPE.SELECT;
    state.periodDates = [
      moment.utc(opts.period.start).toDate(),
      moment.utc(opts.period.end).toDate()
    ];
  } else {
    state.periodType = PERIOD_TYPE.ALL;
  }

  if (opts.format) {
    state.formatType = opts.format as FormatType;
  }

  state.choiceType = opts.choiceLabels ? CHOICE_TYPE.LABELS : CHOICE_TYPE.CODED;
  state.includeArchive = opts.includeArchive ? [CHECKBOX_VALUE.ARCHIVE] : undefined;
  state.includeFiles = opts.files ? [CHECKBOX_VALUE.FILES] : undefined;
  state.includeMetadata = opts.metadata ? [CHECKBOX_VALUE.METADATA] : undefined;
  state.separateMatrixSheets = opts.separateMatrixSheets ?? true;
  state.matrixHeaderColumns = (opts.exportColumnHeadersForMatrix as MatrixHeader) ?? MATRIX_HEADER.COLUMNS;

  if (opts.timezone) {
    state.localTimezoneForSystemDates = true;
    state.selectedTimezone = opts.timezone;
  }

  if (opts.header) {
    const matched = columnHeaders.find(h => h.value === opts.header);
    if (matched) state.selectedColumnHeader = matched;
  }

  return state;
};
